import { useCallback, useEffect, useRef, useState } from "react";
import { modoAnteprima } from "./anteprimaPdf";
import { loadPdfJs, loadPdfDocumentFromBytes } from "./pdfjsLoader";
import { computeFitScale, isRasterLikeSource } from "./pdfFitUtils";

const previewFrameStyle = { position: "absolute", left: 0, top: 0, width: "100%", height: "100%", border: "none" };
const previewMsgStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255,255,255,0.5)",
  fontSize: 13,
  margin: 0,
  padding: "0 16px",
  textAlign: "center",
};

function getUserAgent() {
  return typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
}

/** iOS / iPadOS — native PDF in iframe, DOM often empty. */
function isIosSafariLike() {
  const ua = getUserAgent();
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/**
 * Browsers that render PDFs via a native plugin in blob iframes (DOM often empty).
 * Desktop Chrome/Edge must be included — otherwise we false-positive into PDF.js and show an error.
 * Android Chrome / Samsung / WebView: same as iOS. Brave (any platform) needs iframe probe + PDF.js fallback.
 */
function usesNativePdfPluginWithoutDom() {
  if (isIosSafariLike()) return true;
  const ua = getUserAgent();
  if (!/Android/i.test(ua)) {
    if (/Brave/i.test(ua)) return false;
    if (/Edg\//i.test(ua)) return true;
    if (/Chrome|Chromium/i.test(ua)) return true;
    return false;
  }
  if (/Brave/i.test(ua)) return false;
  if (/Firefox|FxiOS/i.test(ua)) return false;
  return /Chrome|Chromium|CriOS|SamsungBrowser|EdgA/i.test(ua);
}

function shouldProbeIframeForBrokenPdf() {
  return !usesNativePdfPluginWithoutDom();
}

function isIframePdfLikelyBroken(iframe) {
  if (!iframe || !shouldProbeIframeForBrokenPdf()) return false;
  try {
    const doc = iframe.contentDocument;
    if (!doc?.body) return false;
    const pdfEl = doc.querySelector('embed[type*="pdf"], object[type*="pdf"], embed, object');
    if (pdfEl && (pdfEl.offsetWidth > 40 || pdfEl.offsetHeight > 40)) return false;
    const body = doc.body;
    const html = (body.innerHTML || "").replace(/\s/g, "");
    if (body.childElementCount === 0 && html.length === 0) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Desktop: native iframe PDF first, PDF.js only if the preview looks broken.
 * Phones and tablets, and raster uploads (PNG/JPG → PDF): PDF.js from the
 * start (see anteprimaPdf.js).
 */
export function PdfResultPreview({ blobUrl, title = "PDF preview", originalFilename = "" }) {
  const iframeRef = useRef(null);
  const [mode, setMode] = useState(() => modoAnteprima(originalFilename));
  const checkTimerRef = useRef(null);

  const scheduleBrokenCheck = useCallback(() => {
    if (!shouldProbeIframeForBrokenPdf()) return;
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    let attempts = 0;
    const probe = () => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      if (!isIframePdfLikelyBroken(iframe)) return;
      attempts += 1;
      if (attempts >= 3) {
        setMode("pdfjs");
        return;
      }
      checkTimerRef.current = setTimeout(probe, 800);
    };
    checkTimerRef.current = setTimeout(probe, 900);
  }, []);

  useEffect(() => {
    setMode(modoAnteprima(originalFilename));
    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, [blobUrl, originalFilename]);

  if (mode === "pdfjs") {
    return <PdfBlobPreview blobUrl={blobUrl} title={title} originalFilename={originalFilename} />;
  }

  return (
    <iframe
      ref={iframeRef}
      src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
      style={previewFrameStyle}
      title={title}
      onLoad={scheduleBrokenCheck}
      onError={() => setMode("pdfjs")}
    />
  );
}

/**
 * PDF.js canvas stack: raster PDFs, phones and tablets, and desktop browsers
 * whose iframe preview fails. Text documents are drawn one page at a time,
 * as each is about to scroll into view: a hundred pages drawn at once, at
 * twice the screen resolution, would take hundreds of MB, and a phone that
 * runs out of memory reloads the page.
 */
export default function PdfBlobPreview({ blobUrl, title = "PDF preview", originalFilename = "" }) {
  const scrollRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!blobUrl) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    let pdfDoc = null;
    let osservatore = null;

    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        pdfDoc = await loadPdfDocumentFromBytes(pdfjs, await (await fetch(blobUrl)).arrayBuffer());
        if (cancelled) return;

        const container = scrollRef.current;
        if (!container) return;
        container.replaceChildren();

        const containerWidth = Math.max(container.clientWidth || 320, 280);
        const containerHeight = Math.max(container.clientHeight || 480, 280);
        const firstPage = await pdfDoc.getPage(1);
        const firstBase = firstPage.getViewport({ scale: 1 });
        const rasterLike = isRasterLikeSource(originalFilename, firstBase);
        const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

        const tela = async (page, scale, larghezza) => {
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("canvas 2d unavailable");
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.display = "block";
          canvas.style.width = larghezza;
          canvas.style.height = "auto";
          canvas.style.margin = "0 auto";
          canvas.style.background = "#fff";
          ctx.scale(dpr, dpr);
          await page.render({ canvasContext: ctx, viewport }).promise;
          return canvas;
        };

        if (rasterLike) {
          // Images turned into PDFs: every page fitted inside the box.
          for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
            if (cancelled) return;
            const page = await pdfDoc.getPage(pageNum);
            const baseViewport = page.getViewport({ scale: 1 });
            const pageGap = pdfDoc.numPages > 1 ? 4 : 0;
            const maxPageHeight = pdfDoc.numPages > 1
              ? Math.max((containerHeight - pageGap * (pdfDoc.numPages - 1)) / pdfDoc.numPages, 120)
              : containerHeight;
            const scale = computeFitScale(baseViewport, {
              clientWidth: containerWidth,
              clientHeight: maxPageHeight,
            }, 0);
            const canvas = await tela(page, scale, `${baseViewport.width * scale}px`);
            if (pageNum < pdfDoc.numPages) canvas.style.marginBottom = "4px";
            if (!cancelled) container.appendChild(canvas);
          }
          if (!cancelled) setStatus("ready");
          return;
        }

        // Text documents: full-width pages. Until its turn each page is a
        // white box as tall as the first page would be at this width.
        const altezza = Math.round(containerWidth * firstBase.height / firstBase.width);
        const pagine = Array.from({ length: pdfDoc.numPages }, (_, i) => {
          const scatola = document.createElement("div");
          scatola.style.width = "100%";
          scatola.style.height = `${altezza}px`;
          scatola.style.background = "#fff";
          if (i < pdfDoc.numPages - 1) scatola.style.marginBottom = "4px";
          container.appendChild(scatola);
          return { numero: i + 1, scatola, chiesta: false };
        });
        let coda = Promise.resolve();
        const disegna = (voce) => {
          if (voce.chiesta) return;
          voce.chiesta = true;
          coda = coda.then(async () => {
            if (cancelled) return;
            const page = voce.numero === 1 ? firstPage : await pdfDoc.getPage(voce.numero);
            const base = page.getViewport({ scale: 1 });
            const canvas = await tela(page, containerWidth / base.width, "100%");
            if (cancelled) return;
            voce.scatola.style.height = "auto";
            voce.scatola.replaceChildren(canvas);
            if (voce.numero === 1) setStatus("ready");
          }).catch(() => {
            if (!cancelled && voce.numero === 1) setStatus("error");
          });
        };
        if (typeof IntersectionObserver === "undefined") {
          pagine.forEach(disegna);
        } else {
          osservatore = new IntersectionObserver((voci) => {
            for (const v of voci) {
              if (!v.isIntersecting) continue;
              osservatore.unobserve(v.target);
              const voce = pagine.find((p) => p.scatola === v.target);
              if (voce) disegna(voce);
            }
          }, { root: container, rootMargin: "800px 0px" });
          pagine.forEach((p) => osservatore.observe(p.scatola));
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      osservatore?.disconnect();
      pdfDoc?.destroy?.();
    };
  }, [blobUrl, originalFilename]);

  const boxStyle = {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    background: "#000",
  };

  return (
    <>
      <div ref={scrollRef} style={boxStyle} aria-label={title} />
      {status === "loading" && <p style={previewMsgStyle}>Caricamento anteprima…</p>}
      {status === "error" && <p style={previewMsgStyle}>Anteprima PDF non disponibile in questo browser.</p>}
    </>
  );
}
